/**
 * Parser voor BizziPhone-antwoordservice mails. Body bevat een vaste key-value
 * lijst:
 *
 *   Naam van de beller: Sandra de Vries
 *   Telefoonnummer: 0638632249
 *   E-mailadres: shdevries@upcmail.nl
 *   Postcode: 1132GV Volendam
 *
 * Plus een vrije reden bovenaan ("Zij belde om..."). Pure functions, geen IO,
 * geen Salesforce. Wordt gebruikt door:
 * - api/cron/inbox-sync.ts → klant-resolver bij ingestion
 * - api/cron/inbox-backfill-bizziphone.ts → backfill van historische records
 * - api/inbox/doorverwijs-prefill.ts → naam/telefoon/e-mail voor doorverwijs-dialoog
 * - src/components/inbox/CallbackPanel.tsx → telefoonnummer voor bel-knop
 *
 * Nieuwere belnotities zetten de gestructureerde "Gespreksdetails" (Naam van de
 * beller / Telefoonnummer / E-mailadres) alléén in de HTML, met label en waarde
 * op APARTE regels. Daarom: (1) extractField pakt ook een waarde op de volgende
 * regel, en (2) `parseBizziPhoneFields({text, html})` valt terug op de
 * HTML-afgeleide tekst als de plain-text de velden mist.
 */

export interface BizziPhoneFields {
  name: string | null;
  email: string | null;
  phone: string | null;
  postcode: string | null;
  reden: string | null;
}

const FIELD_LABELS = {
  name: ['Naam van de beller', 'Naam'],
  email: ['E-mailadres', 'E-mail', 'Email'],
  phone: ['Telefoonnummer', 'Telefoon', 'Tel'],
  postcode: ['Postcode'],
} as const;

const EMAIL_REGEX = /[\w.+\-]+@[\w-]+(?:\.[\w-]+)+/;

// Spatie/streepje tussen ELK cijferpaar toestaan — BizziPhone levert nummers
// vaak gegroepeerd ("06-12 34 56 78"). NL-nummers zijn 10 cijfers vanaf 0, of
// +31 gevolgd door 9 cijfers.
const PHONE_PATTERNS: RegExp[] = [
  /\+31(?:[\s-]?\d){9}/,
  /\b0(?:[\s-]?\d){9}\b/,
];

const ALL_LABELS: readonly string[] = [
  ...FIELD_LABELS.name,
  ...FIELD_LABELS.email,
  ...FIELD_LABELS.phone,
  ...FIELD_LABELS.postcode,
];

/** Hoofd-parser: levert alle velden uit de body, of nulls als de body leeg is. */
export function parseBizziPhoneBody(text: string | null | undefined): BizziPhoneFields {
  if (!text || !text.trim()) {
    return { name: null, email: null, phone: null, postcode: null, reden: null };
  }
  return {
    name: extractField(text, FIELD_LABELS.name),
    email: extractEmail(text),
    phone: extractPhone(text),
    postcode: extractField(text, FIELD_LABELS.postcode),
    reden: extractReden(text),
  };
}

/**
 * Parse die zowel de plain-text als de HTML van een belnotitie aankan. Parse eerst
 * de tekst (beste 'reden'); vul ontbrekende velden aan uit de HTML-afgeleide tekst,
 * want nieuwere notities zetten Naam/Telefoon/E-mail alléén in de HTML. Tekst wint
 * per veld zodat een bestaande plain-text-notitie identiek geparsed blijft.
 */
export function parseBizziPhoneFields(input: { text?: string | null; html?: string | null }): BizziPhoneFields {
  const fromText = parseBizziPhoneBody(input.text ?? '');
  if (!input.html || (fromText.name && fromText.phone && fromText.email)) return fromText;
  const fromHtml = parseBizziPhoneBody(htmlToPlain(input.html));
  return {
    name: fromText.name ?? fromHtml.name,
    email: fromText.email ?? fromHtml.email,
    phone: fromText.phone ?? fromHtml.phone,
    postcode: fromText.postcode ?? fromHtml.postcode,
    reden: fromText.reden ?? fromHtml.reden,
  };
}

/**
 * Strip HTML naar platte tekst: block-tags worden regeleindes, tags/entities/
 * zero-width-ruis verdwijnen. Zo belanden "Naam van de beller:" en de waarde
 * eronder elk op hun eigen regel, klaar voor extractField.
 */
export function htmlToPlain(html: string | null | undefined): string {
  if (!html) return '';
  return html
    .replace(/<(style|script|head)[\s\S]*?<\/\1>/gi, ' ')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|tr|li|h[1-6]|td|th|table)>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#?[a-z0-9]+;/gi, ' ')
    .replace(/[\u200B-\u200D\uFEFF\u2060\uFFFD]/g, '')
    .replace(/[^\S\n]+/g, ' ')
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .join('\n');
}

/** Is dit een (lege) labelregel? Voorkomt dat een volgend label als waarde wordt gepakt. */
function looksLikeLabelLine(line: string): boolean {
  if (/^[^:]{1,40}:\s*$/.test(line)) return true;
  return ALL_LABELS.some((l) =>
    new RegExp(`^${l.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*:`, 'i').test(line),
  );
}

/**
 * Zoekt "Label:" gevolgd door een waarde op dezelfde regel, of — als die regel na
 * de dubbele punt leeg is (HTML-afgeleide notitie) — op de volgende niet-lege regel
 * mits dat zelf geen label is. Case-insensitive op label, behoud case in waarde.
 */
export function extractField(text: string, labels: readonly string[]): string | null {
  const lines = text.split(/\r?\n/);
  for (const label of labels) {
    const escaped = label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const labelRe = new RegExp(`^\\s*${escaped}\\s*:\\s*(.*)$`, 'i');
    for (let i = 0; i < lines.length; i++) {
      const match = (lines[i] ?? '').match(labelRe);
      if (!match) continue;
      const sameLine = normalizeValue(match[1] ?? '');
      if (sameLine) return sameLine;
      // Waarde op de volgende niet-lege regel (label en waarde op aparte regels).
      for (let j = i + 1; j < lines.length; j++) {
        const next = normalizeValue(lines[j] ?? '');
        if (!next) continue;
        if (!looksLikeLabelLine(next)) return next;
        break; // volgende regel is zelf een label → dit label heeft geen waarde
      }
      break; // dit label gevonden maar zonder waarde → probeer volgend label-alias
    }
  }
  return null;
}

/** Normaliseer een veldwaarde: interne witruimte (ook nbsp) naar één spatie. */
function normalizeValue(raw: string): string {
  return raw.replace(/\s+/g, ' ').trim();
}

/**
 * Extracteert email-adres. Probeert eerst expliciete "E-mailadres:" label
 * (betrouwbaarder bij ondertekeningen), valt terug op eerste @-string in body.
 */
/** Adressen van het antwoordbureau zelf (voettekst) zijn nooit de beller. */
const EIGEN_ADRES = /@bizziphone\.com$/i;

export function extractEmail(text: string): string | null {
  const labeled = extractField(text, FIELD_LABELS.email);
  if (labeled) {
    const m = labeled.match(EMAIL_REGEX)?.[0];
    if (m && !EIGEN_ADRES.test(m)) return m.toLowerCase();
  }
  // Terugval op het eerste adres in de body, maar nooit info@bizziphone.com uit
  // de voettekst ("Ik heb gevraagd naar het e-mailadres, dit liet de beller
  // niet achter", backfill-dry-run 11-09: die notities kregen anders het
  // antwoordbureau als klant).
  for (const m of text.match(new RegExp(EMAIL_REGEX.source, 'g')) || []) {
    if (!EIGEN_ADRES.test(m)) return m.toLowerCase();
  }
  return null;
}

/**
 * Extracteert NL telefoonnummer. Probeert eerst "Telefoonnummer:" label,
 * valt terug op patroonzoekgang door de hele body. Spaties en streepjes
 * worden gestript voor consistente downstream-matching.
 */
export function extractPhone(text: string): string | null {
  const labeled = extractField(text, FIELD_LABELS.phone);
  if (labeled) {
    const cleaned = matchPhone(labeled);
    if (cleaned) return cleaned;
  }
  return matchPhone(text);
}

function matchPhone(input: string): string | null {
  for (const p of PHONE_PATTERNS) {
    const found = input.match(p)?.[0];
    if (found) return found.replace(/[\s-]/g, '');
  }
  return null;
}

/**
 * Pakt de eerste regels boven de gestructureerde key-value lijst. Stopt op
 * een herkend veld-label of na 3 regels. Output capped op 200 chars.
 */
export function extractReden(text: string): string | null {
  const labelStarts = [
    ...FIELD_LABELS.name,
    ...FIELD_LABELS.email,
    ...FIELD_LABELS.phone,
    ...FIELD_LABELS.postcode,
  ];
  const lines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  const collected: string[] = [];
  for (const line of lines) {
    const isLabelLine = labelStarts.some(label => {
      const re = new RegExp(`^${label.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*:`, 'i');
      return re.test(line);
    });
    if (isLabelLine) break;
    collected.push(line);
    if (collected.length >= 3) break;
  }
  if (collected.length === 0) return null;
  const joined = collected.join(' ').trim();
  if (!joined) return null;
  return joined.length > 200 ? joined.slice(0, 200) + '…' : joined;
}
